import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';

/* ------------------------------------------------------------------ */
/*  STAMP FIT 레벨 뱃지 쇼케이스                                         */
/*  원본 standalone HTML(badge3_cleaned.html)의 색·폰트·치수를          */
/*  앱 브랜드와 독립적으로 그대로 재현 + 스크롤 리빌/플로팅 애니메이션    */
/* ------------------------------------------------------------------ */

const BLUE = '#2453d4';

/* 스크롤 리빌 래퍼 (IntersectionObserver, prefers-reduced-motion 가드) */
type RevealVariant = 'up' | 'right' | 'scale' | 'fade';

const VARIANT_HIDDEN: Record<RevealVariant, string> = {
  up: 'opacity-0 translate-y-10',
  right: 'opacity-0 translate-x-12',
  scale: 'opacity-0 scale-95',
  fade: 'opacity-0',
};

function Reveal({
  children,
  className = '',
  style,
  variant = 'up',
  delay = 0,
  as: Tag = 'div' as ElementType,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  variant?: RevealVariant;
  delay?: number;
  as?: ElementType;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref as never}
      style={{ transitionDelay: `${delay}ms`, ...style, willChange: visible ? 'auto' : 'transform, opacity' }}
      className={`transition-all duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
        visible ? 'opacity-100 translate-x-0 translate-y-0 scale-100' : VARIANT_HIDDEN[variant]
      } ${className}`}
    >
      {children}
    </Tag>
  );
}

/* 데이터 ----------------------------------------------------------- */
const FEATURES = [
  {
    num: '01',
    title: 'Identity',
    desc: '교육 이수 과목, 이수 시간 및 교육 연결성 등을 종합한 지표를 통해 지도자별 전문성을 시각화합니다.',
  },
  {
    num: '02',
    title: '커리어 브랜딩',
    desc: '나의 전문 역량을 시각적으로 표현하여 전문 강사로 브랜딩하고, 나의 노력을 더욱 가치 있게 보여줍니다.',
  },
  {
    num: '03',
    title: '피트니스 가이드',
    desc: '전문 강사로 성장하기 위한 단계별 교육 방향을 제시합니다.',
  },
  {
    num: '04',
    title: 'AI 기반 교육 추천',
    desc: '현재까지의 교육 이력을 기반으로 다음 단계 도약을 위한 맞춤형 교육 정보를 추천합니다.',
  },
];

interface BadgeType {
  icon: string;
  title: string;
  levels: { label: string; on: boolean }[];
  courses: { label: string; on: boolean }[];
}

const TYPES: BadgeType[] = [
  {
    icon: '🏅',
    title: '기능성 트레이닝',
    levels: [
      { label: '마스터', on: false },
      { label: '전문가', on: true },
      { label: '강사', on: false },
    ],
    courses: [
      { label: 'TRX 교육 수료', on: true },
      { label: '바이퍼, 아쿠아백 교육 수료', on: false },
      { label: '케틀벨 교육 수료', on: true },
      { label: '클럽벨 교육 수료', on: true },
      { label: '메디신볼 교육 수료', on: false },
      { label: 'BOSU 교육 수료', on: true },
    ],
  },
  {
    icon: '🎓',
    title: '신체 정렬',
    levels: [
      { label: '마스터', on: true },
      { label: '전문가', on: false },
      { label: '강사', on: false },
    ],
    courses: [
      { label: '교정 운동 관련 교육 수료', on: true },
      { label: '슈로스 운동 교육 수료', on: true },
      { label: '기능해부학 교육 수료', on: true },
      { label: '키네시올로지 교육 수료', on: true },
    ],
  },
  {
    icon: '⭐',
    title: '자세 움직임',
    levels: [
      { label: '마스터', on: true },
      { label: '전문가', on: false },
      { label: '강사', on: false },
    ],
    courses: [
      { label: '필라테스 강사 자격증 취득', on: true },
      { label: '요가 강사 자격증 취득', on: true },
      { label: '바레 강사 자격증 취득', on: true },
      { label: '자이로토닉 강사 자격증 취득', on: true },
    ],
  },
  {
    icon: '💎',
    title: '산전·산후 트레이닝',
    levels: [
      { label: '마스터', on: false },
      { label: '전문가', on: false },
      { label: '강사', on: true },
    ],
    courses: [
      { label: '산전&산후 운동 교육 수료', on: true },
      { label: '필라테스 강사 자격증 취득', on: true },
      { label: '생활스포츠지도사 자격증 취득', on: false },
      { label: '건강운동관리사 자격증 취득', on: false },
    ],
  },
];

/* 공용 배지 라벨 (점 + 텍스트) */
function BadgeLabel({
  children,
  tone = 'light',
}: {
  children: ReactNode;
  tone?: 'light' | 'dark';
}) {
  const dark = tone === 'dark';
  return (
    <span
      className="mb-5 inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[14px] font-bold"
      style={{
        background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(42,92,255,0.1)',
        color: dark ? '#bfdbfe' : '#2453d4',
      }}
    >
      <span
        className="db-dot h-2 w-2 rounded-full"
        style={{ background: dark ? '#bfdbfe' : BLUE }}
      />
      {children}
    </span>
  );
}

export default function DigitalBadge() {
  return (
    <div
      className="overflow-hidden"
      style={{
        fontFamily: '"Inter", "Noto Sans KR", sans-serif',
        color: '#101828',
        lineHeight: 1.6,
        // 원본 standalone HTML과 동일한 글자 렌더링
        WebkitFontSmoothing: 'auto',
        MozOsxFontSmoothing: 'auto',
        background: '#f6f7fb',
      }}
    >
      <style>{`
        @keyframes db-float { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-9px) rotate(4deg); } }
        @keyframes db-blob { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-34px,26px) scale(1.12); } }
        @keyframes db-pulse { 0% { box-shadow: 0 0 0 0 rgba(36,83,212,0.35); } 70% { box-shadow: 0 0 0 10px rgba(36,83,212,0); } 100% { box-shadow: 0 0 0 0 rgba(36,83,212,0); } }
        .db-blob { animation: db-blob 18s ease-in-out infinite; }
        .db-dot { animation: db-pulse 2.4s ease-in-out infinite; }
        .db-float { animation: db-float 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .db-blob, .db-dot, .db-float { animation: none !important; }
        }
      `}</style>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden pb-[90px] pt-[80px]">
        {/* 배경 블롭 */}
        <div
          className="db-blob pointer-events-none absolute -right-[180px] -top-[260px] -z-0 h-[680px] w-[680px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(36,83,212,0.22), transparent 65%)',
          }}
        />

        <div className="relative z-10 mx-auto grid w-[min(1180px,100%)] items-center gap-[60px] lg:grid-cols-[1.05fr_0.95fr]">
          {/* 좌측 카피 */}
          <div>
            <Reveal variant="fade">
              <BadgeLabel>LEVEL BADGE</BadgeLabel>
            </Reveal>

            <Reveal variant="up" delay={80}>
              <h1
                className="mb-[26px] font-black"
                style={{
                  fontSize: 'clamp(48px, 7vw, 84px)',
                  lineHeight: 1.02,
                  letterSpacing: '-0.07em',
                }}
              >
                당신의 전문성을
                <br />
                한눈에 <span style={{ color: BLUE }}>증명하세요</span>
              </h1>
            </Reveal>

            <Reveal variant="up" delay={180}>
              <p
                className="mb-[34px] max-w-[620px] text-[20px] [word-break:keep-all]"
                style={{ color: '#475467' }}
              >
                나의 피트니스 교육 여정을 인정받으세요.
              </p>
            </Reveal>

            <Reveal variant="up" delay={280}>
              <div
                className="flex flex-wrap gap-[18px] text-[14px] font-semibold"
                style={{ color: '#667085' }}
              >
                {['교육 업적', '전문가 브랜딩', '커리어 지표', '교육 자동 추천'].map((t) => (
                  <span key={t}>
                    <span className="mr-1.5 font-black" style={{ color: BLUE }}>
                      ✓
                    </span>
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          {/* 우측 다크 패널 (CORE FEATURES) */}
          <Reveal variant="right" delay={120}>
            <div
              className="relative overflow-hidden rounded-[42px] p-[34px]"
              style={{
                background:
                  'radial-gradient(circle at 85% 0%, rgba(111,154,255,0.36), transparent 34%), linear-gradient(145deg, #101828, #1d2939)',
                boxShadow: '0 32px 80px rgba(16,24,40,0.26)',
              }}
            >
              {/* 내부 테두리 */}
              <div
                className="pointer-events-none absolute inset-[18px] rounded-[32px]"
                style={{ border: '1px solid rgba(255,255,255,0.12)' }}
              />

              <div className="relative z-[2] mb-6 text-white">
                <BadgeLabel tone="dark">CORE FEATURES</BadgeLabel>
              </div>

              <div className="relative z-[2] grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                {FEATURES.map((f, i) => (
                  <Reveal
                    key={f.num}
                    variant="up"
                    delay={200 + i * 110}
                    className="min-h-[172px] rounded-[24px] p-[22px] text-white transition duration-[250ms] hover:-translate-y-1.5"
                    style={{
                      border: '1px solid rgba(255,255,255,0.13)',
                      background: 'rgba(255,255,255,0.09)',
                    }}
                  >
                    <div className="mb-3 text-[14px] font-black" style={{ color: '#93c5fd' }}>
                      {f.num}
                    </div>
                    <h3
                      className="mb-2.5 text-[20px]"
                      style={{ lineHeight: 1.25, letterSpacing: '-0.04em' }}
                    >
                      {f.title}
                    </h3>
                    <p className="text-[14px] [word-break:keep-all]" style={{ color: '#d0d5dd', lineHeight: 1.55 }}>
                      {f.desc}
                    </p>
                  </Reveal>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== BADGE TYPES ===== */}
      <section className="py-[96px]">
        <div className="mx-auto w-[min(1180px,100%)]">
          <Reveal variant="up" className="mb-[58px] text-center">
            <div className="flex justify-center">
              <BadgeLabel>BADGE TYPES</BadgeLabel>
            </div>
            <h2
              className="mb-[18px] font-black"
              style={{
                fontSize: 'clamp(34px, 5vw, 56px)',
                lineHeight: 1.15,
                letterSpacing: '-0.06em',
              }}
            >
              다양한 유형의 레벨 뱃지 예시
            </h2>
            <p
              className="mx-auto max-w-[720px] text-[18px] [word-break:keep-all]"
              style={{ color: '#667085' }}
            >
              교육 수료부터 자격 인증, 전문 분야, 업적 달성까지 다양한 성취를 뱃지로 표현할 수 있습니다.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 gap-[22px] sm:grid-cols-2 lg:grid-cols-4">
            {TYPES.map((type, i) => (
              <Reveal
                key={type.title}
                variant="up"
                delay={i * 120}
                className="group relative min-h-[350px] overflow-hidden rounded-[34px] bg-white p-[34px_28px]"
                style={{
                  border: '1px solid rgba(16,24,40,0.08)',
                  boxShadow: '0 18px 48px rgba(16,24,40,0.06)',
                }}
              >
                {/* 우하단 원형 장식 */}
                <span
                  className="pointer-events-none absolute -bottom-[60px] -right-[60px] h-[150px] w-[150px] rounded-full"
                  style={{ background: 'rgba(36,83,212,0.1)' }}
                />

                {/* 육각 아이콘 */}
                <div
                  className="db-float mb-6 flex h-[82px] w-[74px] items-center justify-center text-[30px] text-white"
                  style={{
                    clipPath: 'polygon(50% 0%, 94% 25%, 94% 75%, 50% 100%, 6% 75%, 6% 25%)',
                    background: 'linear-gradient(145deg, #2453d4, #6d8cff)',
                    animationDelay: `${i * 0.6}s`,
                  }}
                >
                  {type.icon}
                </div>

                <h3 className="relative mb-2 text-[22px]" style={{ letterSpacing: '-0.04em' }}>
                  {type.title}
                </h3>

                <strong className="relative mb-[18px] block text-[14px] font-extrabold" style={{ lineHeight: 1.5 }}>
                  {type.levels.map((lv, idx) => (
                    <span key={lv.label}>
                      <span style={{ color: lv.on ? BLUE : '#98a2b3' }}>{lv.label}</span>
                      {idx < type.levels.length - 1 && <br />}
                    </span>
                  ))}
                </strong>

                <ul className="relative grid list-disc gap-[9px] pl-[18px] text-[15px] font-semibold" style={{ color: '#667085' }}>
                  {type.courses.map((c) => (
                    <li key={c.label} style={c.on ? { color: '#0066ff' } : undefined}>
                      {c.label}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer
        className="py-[42px] text-[14px]"
        style={{ borderTop: '1px solid rgba(16,24,40,0.08)', color: '#667085' }}
      >
        <div className="mx-auto flex w-[min(1180px,100%)] flex-wrap justify-between gap-5">
          <div>© 2026 STAMP FIT. All rights reserved.</div>
          <div>Authenticate Your Fitness Journey</div>
        </div>
      </footer>
    </div>
  );
}
